/**
 * Export state management
 */

import { create } from 'zustand'
import { ExportSettings, ExportProgress, DEFAULT_EXPORT_SETTINGS } from '@/lib/export/types'
import { exportVideo, downloadBlob } from '@/lib/export/video-encoder'

interface ExportState {
    // State
    isExporting: boolean
    isModalOpen: boolean
    progress: ExportProgress | null
    error: string | null
    lastExportBlob: Blob | null
    settings: ExportSettings

    // Actions
    openModal: () => void
    closeModal: () => void
    setSettings: (settings: Partial<ExportSettings>) => void
    startExport: () => Promise<void>
    cancelExport: () => void
    downloadLastExport: (filename?: string) => void
    reset: () => void
}

// Abort controller for cancellation
let abortController: AbortController | null = null

export const useExportStore = create<ExportState>((set, get) => ({
    // Initial state
    isExporting: false,
    isModalOpen: false,
    progress: null,
    error: null,
    lastExportBlob: null,
    settings: DEFAULT_EXPORT_SETTINGS,

    // Actions
    openModal: () => set({ isModalOpen: true, error: null }),

    closeModal: () => {
        const { isExporting } = get()
        if (!isExporting) {
            set({ isModalOpen: false })
        }
    },

    setSettings: (updates) => set((state) => ({
        settings: { ...state.settings, ...updates }
    })),

    startExport: async () => {
        const { settings } = get()

        set({
            isExporting: true,
            error: null,
            progress: {
                stage: 'initializing',
                progress: 0,
                currentFrame: 0,
                totalFrames: 0,
                estimatedTimeRemaining: 0,
                message: 'Starting export...',
            },
        })

        abortController = new AbortController()

        try {
            const blob = await exportVideo(settings, (progress) => {
                set({ progress })
            })

            set({
                isExporting: false,
                lastExportBlob: blob,
                progress: {
                    stage: 'complete',
                    progress: 100,
                    currentFrame: 0,
                    totalFrames: 0,
                    estimatedTimeRemaining: 0,
                    message: 'Export complete!',
                },
            })

            // Auto-download with correct extension
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')
            const extension = settings.format === 'webm' ? 'webm' : 'mp4'
            downloadBlob(blob, `screenrecording_${timestamp}.${extension}`)

        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                set({
                    isExporting: false,
                    error: 'Export cancelled',
                    progress: null,
                })
            } else {
                console.error('Export failed:', error)
                set({
                    isExporting: false,
                    error: error instanceof Error ? error.message : 'Export failed',
                    progress: {
                        stage: 'error',
                        progress: 0,
                        currentFrame: 0,
                        totalFrames: 0,
                        estimatedTimeRemaining: 0,
                        message: error instanceof Error ? error.message : 'Export failed',
                    },
                })
            }
        } finally {
            abortController = null
        }
    },

    cancelExport: () => {
        if (abortController) {
            abortController.abort()
        }
        set({
            isExporting: false,
            progress: null,
        })
    },

    downloadLastExport: (filename) => {
        const { lastExportBlob, settings } = get()
        if (lastExportBlob) {
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')
            const extension = settings.format === 'webm' ? 'webm' : 'mp4'
            downloadBlob(lastExportBlob, filename || `screenrecording_${timestamp}.${extension}`)
        }
    },

    reset: () => set({
        isExporting: false,
        isModalOpen: false,
        progress: null,
        error: null,
        lastExportBlob: null,
        settings: DEFAULT_EXPORT_SETTINGS,
    }),
}))
